/** @format */

import {filterData} from 'components/Table/core/helpers/filterData';
import {TanStackTableData} from 'components/Table/core/types';

import {safeDivide} from 'helpers/numberHelpers';

import {CampaignStatFromQuery, mapCampaignStatistics} from './mapCampaignStatistics';

interface CampaignStatisticsTotals {
  // Абсолютные метрики — сырые суммы из stats.<field>, БЕЗ округления.
  // Округление spent через roundFloatNumber(value, 0) выполняется в UI-слое
  // (компонент TotalRow), перед передачей в FormattedCell/divideBySpaces.
  // Остальные поля — целочисленные в API, дополнительного округления не требуют.
  spent: number; // расход, ₽ (сырая сумма; округляется до 0 знаков в UI)
  impressions: number; // показы, шт.
  clicks: number; // клики, шт.
  conversions: number; // ЛИДЫ, шт. (НЕ заказы)
  ePurchaseConversions: number; // ЗАКАЗЫ, шт. (e-purchase conversions)
  revenue: number; // доход, ₽

  // Производные метрики — рассчитаны из сырых сумм абсолютных метрик через
  // safeDivide. В контракте — НЕокруглённые значения; финальное округление
  // до нужного числа знаков выполняется в UI-слое (FR-2).
  ctr: number; // = safeDivide(clicks, impressions) * 100; в UI округление до 1 знака
  cpc: number; // = safeDivide(spent, clicks);             в UI округление до 0 знаков
  cpl: number; // = safeDivide(spent, conversions);        в UI округление до 0 знаков (делится на conversions = ЛИДЫ, не на ePurchaseConversions = ЗАКАЗЫ)
  drr: number; // = safeDivide(spent, revenue) * 100;      в UI округление до 1 знака
}

interface AggregateCampaignStatisticsOptions {
  data: ReadonlyArray<CampaignStatFromQuery>;
  mediaplanType?: string | null;
  search?: string;
}

interface AggregateCampaignStatisticsResult {
  tableData: TanStackTableData[];
  totals: CampaignStatisticsTotals | null;
}

const SEARCH_BY_COLUMN_IDS = ['title', 'id'];

const computeTotals = (
  rawFiltered: ReadonlyArray<CampaignStatFromQuery>,
): CampaignStatisticsTotals | null => {
  if (rawFiltered.length === 0) {
    return null;
  }

  let spent = 0;
  let impressions = 0;
  let clicks = 0;
  let conversions = 0;
  let ePurchaseConversions = 0;
  let revenue = 0;

  for (const stats of rawFiltered) {
    spent += stats.spent;
    impressions += stats.impressions;
    clicks += stats.clicks;
    conversions += stats.conversions;
    ePurchaseConversions += stats.ePurchaseConversions;
    revenue += stats.revenue;
  }

  const ctr = safeDivide(clicks, impressions) * 100;
  const cpc = safeDivide(spent, clicks);
  const cpl = safeDivide(spent, conversions);
  const drr = safeDivide(spent, revenue) * 100;

  return {
    spent,
    impressions,
    clicks,
    conversions,
    ePurchaseConversions,
    revenue,
    ctr,
    cpc,
    cpl,
    drr,
  };
};

const aggregateCampaignStatistics = ({
  data,
  mediaplanType,
  search,
}: AggregateCampaignStatisticsOptions): AggregateCampaignStatisticsResult => {
  // Шаг 1: переиспользуем существующий маппер «как есть» (вариант (a), решение
  // P1-new-3). Маппер сразу применяет mediaplanType-фильтр и round per-row.
  const mapped = mapCampaignStatistics({data, mediaplanType});

  // Шаг 2: поиск по уже замаппленным данным, переиспользуем filterData
  // (решение P1-new-1). При пустом search возвращаем все строки.
  const tableData = search
    ? filterData({data: mapped, search, searchByColumnIds: SEARCH_BY_COLUMN_IDS})
    : mapped;

  // Шаг 3: построить rawFiltered, согласованный с tableData. Сначала зеркально
  // применяем тот же mediaplanType-фильтр, что внутри mapCampaignStatistics
  // (одна строка дублируется сознательно — без этого пришлось бы дважды маппить
  // данные либо включать сырые объекты в результат маппера).
  const rawByMediaplan = mediaplanType
    ? data.filter(stats => stats.mediaplanType.toLowerCase() === mediaplanType.toLowerCase())
    : data;

  // Сопоставление по campaignId (id у замаппленной строки = campaignId у raw).
  const idSet = new Set(tableData.map(row => String(row.id)));
  const rawFiltered = rawByMediaplan.filter(stats => idSet.has(stats.campaignId));

  // Шаг 4: считаем тотал на сырых числах (P0-3 — округление spent один раз
  // над суммой выполняется уже в UI-слое, см. TotalRow).
  const totals = computeTotals(rawFiltered);

  return {tableData, totals};
};

export {aggregateCampaignStatistics};
export type {CampaignStatisticsTotals, AggregateCampaignStatisticsResult};
